<template>
    <div>
        <v-container fluid grid-list-md>
            <v-layout>
                <v-flex xs2 sm2 md2 d-flex>
                    <v-select
                            @change="rangeChanged"
                            solo
                            item-value="id"
                            item-text="name"
                            :items="ranges"
                            :label="trans('app.statistics.range')"
                            v-model="selectedRange"
                    ></v-select>
                </v-flex>
                <v-flex xs2 sm2 md2 v-if="selectedRange === 'custom'">
                    <v-menu
                            v-model="showStartDateInput"
                            :close-on-content-click="true"
                            :nudge-right="40"
                            lazy
                            transition="scale-transition"
                            offset-y
                            full-width
                            min-width="290px"
                    >
                        <template v-slot:activator="{ on }">
                            <v-text-field
                                    v-model="filters.start_date"
                                    readonly
                                    single-line
                                    hide-details
                                    solo
                                    v-on="on"
                            ></v-text-field>
                        </template>
                        <v-date-picker :max="maxStartDate" v-model="filters.start_date"
                                       @input="showStartDateInput = false"
                                       locale="pl-pl"></v-date-picker>
                    </v-menu>
                </v-flex>
                <v-flex xs2 sm2 md2 d-flex v-if="selectedRange === 'custom'">
                    <v-menu
                            v-model="showEndDateInput"
                            :close-on-content-click="true"
                            :nudge-right="40"
                            lazy
                            transition="scale-transition"
                            offset-y
                            full-width
                            min-width="290px"
                    >
                        <template v-slot:activator="{ on }">
                            <v-text-field
                                    v-model="filters.end_date"
                                    readonly
                                    single-line
                                    hide-details
                                    solo
                                    v-on="on"
                            ></v-text-field>
                        </template>
                        <v-date-picker v-model="filters.end_date" @input="showEndDateInput = false"
                                       locale="pl-pl" :min="minEndDate"></v-date-picker>
                    </v-menu>
                </v-flex>
                <v-flex xs2 sm2 md2 d-flex v-if="selectedRange === 'month'">
                    <v-menu
                            v-model="showMonthInput"
                            :close-on-content-click="true"
                            :nudge-right="40"
                            lazy
                            transition="scale-transition"
                            offset-y
                            full-width
                            min-width="290px"
                    >
                        <template v-slot:activator="{ on }">
                            <v-text-field
                                    v-model="filters.month"
                                    readonly
                                    single-line
                                    hide-details
                                    solo
                                    v-on="on"
                            ></v-text-field>
                        </template>
                        <v-date-picker v-model="filters.month" @input="showMonthInput = false"
                                       locale="pl-pl" type="month"></v-date-picker>
                    </v-menu>
                </v-flex>
                <v-flex xs2 sm2 md2 d-flex v-if="selectedRange === 'year'">
                    <v-text-field v-model="filters.year"
                                  type="number"
                                  :label="trans('app.statistics.year')"
                                  single-line
                                  hide-details
                                  solo
                    ></v-text-field>
                </v-flex>
                <v-flex xs2 sm2 md2 d-flex v-if="enableSubscriptionType">
                    <v-select
                            @change="subscriptionTypeChanged"
                            solo
                            item-value="id"
                            item-text="name"
                            :items="subscriptionTypes"
                            :label="trans('app.subscription_type')"
                            v-model="filters.subscription_type"
                    ></v-select>
                </v-flex>
                <v-flex xs1 sm1 md1 d-flex>
                  <slot name="header_actions"></slot>
                </v-flex>
            </v-layout>
        </v-container>
        <v-data-table
                :disable-initial-sort="true"
                :headers="headers"
                :items="items"
                :pagination.sync="pagination"
                :total-items="total"
                :loading="isLoading"
                :rows-per-page-items="[10,25,50,100]"
                :no-data-text="trans('app.search.no_data')"
        >
            <v-progress-linear slot="progress" color="blue" indeterminate></v-progress-linear>
            <template slot="items" slot-scope="props">
                <slot name="content" :item="props.item"></slot>
            </template>
            <template slot="no-data">
                <div class="text-center">
                    <h3>{{ 'app.search.no_results' | trans }}</h3>
                    <v-btn color="primary" @click="resetSearch">{{ 'app.reset' | trans }}</v-btn>
                </div>
            </template>
        </v-data-table>
        <div class="text-xs-center pt-2 pb-4">
            <v-pagination v-model="pagination.page" total-visible=10 :length="pages"></v-pagination>
        </div>
    </div>
</template>
<script>
import {stringify} from "qs";
import debounce from 'lodash/debounce';
import moment from 'moment';
import {trans} from "../../../app";
import {SYSTEM_DATE} from "../../../utils/dateFormatter";

export default {
    props: {
        url: String,
        type: String,
        headers: Array,
        defaultSortBy: {
            type: String,
            default: 'date',
        },
        enableSubscriptionType: {
            type: Boolean,
            default: false,
        },
    },
    data: () => ({
        filters: {
            start_date: null,
            end_date: null,
            month: null,
            year: null,
            subscription_type: null,
        },
        pagination: {
            rowsPerPage: 50,
            sortBy: 'date',
            descending: false,
        },
        total: 0,
        items: [],
        isLoading: true,
        pages: 0,
        showStartDateInput: false,
        showEndDateInput: false,
        showMonthInput: false,
        selectedRange: 'month',
        subscriptionTypes: [
            {
                id: 'all',
                name: trans('app.all'),
            },
            {
                id: 'premium',
                name: trans('app.statistics.premium_total'),
            },
            {
                id: 'premium_30_days',
                name: trans('app.statistics.premium_30_days'),
            },
            {
                id: 'premium_180_days',
                name: trans('app.statistics.premium_180_days'),
            },
            {
                id: 'premium_365_days',
                name: trans('app.statistics.premium_365_days'),
            },
            {
                id: 'standard',
                name: trans('app.statistics.standard_total'),
            },
            {
                id: 'standard_30_days',
                name: trans('app.statistics.standard_30_days'),
            },
            {
                id: 'standard_180_days',
                name: trans('app.statistics.standard_180_days'),
            },
            {
                id: 'standard_365_days',
                name: trans('app.statistics.standard_365_days'),
            },
            {
                id: 'student',
                name: trans('app.statistics.student_total'),
            },
            {
                id: 'student_30_days',
                name: trans('app.statistics.student_30_days'),
            },
            {
                id: 'student_180_days',
                name: trans('app.statistics.student_180_days'),
            },
            {
                id: 'student_365_days',
                name: trans('app.statistics.student_365_days'),
            },
        ],
        ranges: [
            {
                id: 'custom',
                name: trans('app.statistics.custom'),
            },
            {
                id: 'month',
                name: trans('app.statistics.month'),
            },
            {
                id: 'year',
                name: trans('app.statistics.year'),
            },
        ],
    }),
    watch: {
        filters: {
            handler() {
                this.debounceFetch();
            },
            deep: true,
        },
        pagination: {
            handler(newValue, oldValue) {
                if (oldValue.page !== undefined) {
                    this.fetchData();
                }
            },
            deep: true,
        },
    },
    created() {
        this.pagination.sortBy = this.defaultSortBy;
        this.filters.start_date = moment().startOf('month').format(SYSTEM_DATE);
        this.filters.end_date = moment().format(SYSTEM_DATE);
        this.filters.month = moment().format('YYYY-MM');
        this.filters.year = moment().format('YYYY');
        this.debounceFetch = debounce(this.fetchData, 300);
        if (this.enableSubscriptionType) {
            this.filters.subscription_type = 'all';
        }
    },
    methods: {
        resetSearch() {
            this.selectedRange = 'month';
            this.filters = {
                start_date: moment().startOf('month').format(SYSTEM_DATE),
                end_date: moment().format(SYSTEM_DATE),
                month: moment().format('YYYY-MM'),
                year: moment().format('YYYY'),
                subscription_type: this.enableSubscriptionType ? 'all' : null,
            };
            this.pagination.sortBy = this.defaultSortBy;
            this.pagination.descending = false;
        },
        async fetchData() {
            this.isLoading = true;
            const {sortBy, descending, page, rowsPerPage} = this.pagination;
            let aggregate = false;
            if (this.selectedRange === 'year') {
                aggregate = true;
                const year = moment(this.filters.year, 'YYYY');
                if (year.format(SYSTEM_DATE) === 'Invalid date') {
                    this.isLoading = false;
                    return;
                }
                this.filters.start_date = year.startOf('year').format(SYSTEM_DATE)
                this.filters.end_date = year.endOf('year').format(SYSTEM_DATE);
            }

            if (this.selectedRange === 'month') {
                const month = moment(this.filters.month, 'YYYY-MM');
                this.filters.start_date = month.startOf('month').format(SYSTEM_DATE);
                this.filters.end_date = month.endOf('month').format(SYSTEM_DATE);
            }

            let query = {
                type: this.type,
                aggregate: aggregate,
                page: page,
                perPage: rowsPerPage,
                orderBy: sortBy,
                dir: descending ? 'desc' : 'asc',
                ...this.filters,
            };

            const response = await axios.get(`${this.url}?${stringify(query, {skipNulls: true})}`);

            const {data, meta} = response.data;
            this.items = data;
            this.total = meta.total;
            this.pages = meta.last_page;
            this.isLoading = false;
            this.$emit('fetched', this.items);
        },
        rangeChanged() {
            switch (this.selectedRange) {
                case 'custom':
                    this.showStartDateInput = false;
                    this.showEndDateInput = false;
                    return;
                case 'month':
                    this.filters.month = moment().format('YYYY-MM');
                    this.showMonthInput = false;
                    break;
                case 'year':
                    this.filters.year = moment().format('YYYY');
                    break;
            }

            this.fetchData();
        },
        subscriptionTypeChanged() {
            this.fetchData();
        },
        downloadUsersList() {
            const {sortBy, descending} = this.pagination;
            let query = {
                type: this.type,
                orderBy: sortBy,
                dir: descending ? 'desc' : 'asc',
                ...this.filters,
            };
            axios.get(`/v2/statistics/users_list?${stringify(query, {skipNulls: true})}`, {
                headers: {
                    'Accept': 'text/csv',
                },
                responseType: 'arraybuffer',
            }).then(({data}) => {
                const downloadUrl = window.URL.createObjectURL(new Blob([data]));
                const link = document.createElement('a');
                link.href = downloadUrl;
                const fileName = `users_list_${this.filters.start_date}_${this.filters.end_date}.csv`;
                link.setAttribute(
                    'download',
                    fileName,
                );
                document.body.appendChild(link);
                link.click();
                link.remove();
                this.$emit('usersListDownloaded');
            }).catch((error) => {
                this.sentryCaptureException(error);
                this.$emit('usersListDownloadError');
            });
        }
    },
    computed: {
        maxStartDate() {
            if (this.filters.end_date) {
                return moment(this.filters.end_date).add(1, 'day').toISOString().slice(0, 10);
            }
            return moment().toISOString().slice(0, 10);
        },
        minEndDate() {
            if (this.filters.start_date) {
                return moment(this.filters.start_date).add(1, 'day').toISOString().slice(0, 10);
            }
            return moment().toISOString().slice(0, 10);
        },
    }
}
</script>
